import React, { useMemo, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Button, Drawer, Modal, Radio, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import range from 'lodash/range';
import { dayFormatType, formatDay } from '~helpers/day';
import { sortDirectionMapping } from '~constants';
import { herculesBookingStatus, bookingJourneyType } from 'common/constants/booking';
import TableSearchInput from 'common/components/select-filter/table-search-input';
import generatePath from '~settings/routing';
import BookingTable from '../components/table';
import { QUERY_LISTORDERS } from '../queries';
import { handleExportToExcel } from '../utils/export-excel';
import CommissionReport from '../components/commission-report';
import { colors } from 'common/constants/colors';
import { convertEmptyArrayToNull } from 'common/helpers/convertEmptyArrayToNull';

export default function MessageProperty({
  t,
  bookingJourneys = [bookingJourneyType.SEMI_AUTOMATIC],
}) {
  const history = useHistory();
  const [tableData, setTableData] = useState();
  const [filters, setFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [visibleReport, setVisibleReport] = useState(false);
  const [visible, setVisible] = useState(false);
  const [checkedIndex, setCheckedIndex] = useState(0);

  const statusFilter = useMemo(() => {
    const status = herculesBookingStatus;
    return Object.keys(status).map(status => ({
      text: t(`cms.booking.message_property.table.booking_status.${status.toLocaleLowerCase()}`),
      value: status.toUpperCase(),
    }));
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        title: t('cms.booking.message_property.table.landlord'),
        dataIndex: 'landlordName',
        key: 'landlordName',
        width: 228,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.node?.landlord?.name ?? '-'}</span>
        ),
      },
      {
        title: t('cms.booking.message_property.table.booking_number'),
        dataIndex: 'bookingNumber',
        key: 'bookingNumber',
        width: 184,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.node?.referenceId ?? '-'}</span>
        ),
      },
      {
        title: t('cms.booking.message_property.table.student_name'),
        dataIndex: 'studentName',
        key: 'studentName',
        width: 184,
        filterIcon: () => (
          <SearchOutlined
            style={{ color: filters?.studentName ? colors.color_keppel : undefined }}
          />
        ),
        filterDropdown: ({ confirm }) => (
          <TableSearchInput
            value={filters.studentName}
            onClick={value => {
              confirm();
              setFilters(pre => ({ ...pre, studentName: value }));
              handleTableChange({ current: 1 }, { ...filters, studentName: value });
            }}
          />
        ),
        render: (_, record) => (
          <div className="transfer__table-field">{`${record?.node?.student?.fullName}` || '-'}</div>
        ),
      },
      {
        title: t('cms.booking.message_property.table.snapshot_student_name'),
        dataIndex: 'snapshotStudentName',
        key: 'snapshotStudentName',
        width: 184,
        filter: true,
        filterIcon: () => (
          <SearchOutlined
            style={{ color: filters?.passportFullName ? colors.color_keppel : undefined }}
          />
        ),
        filterDropdown: ({ confirm }) => (
          <TableSearchInput
            value={filters.passportFullName}
            onClick={value => {
              confirm();
              setFilters(pre => ({ ...pre, passportFullName: value }));
              handleTableChange({ current: 1 }, { ...filters, passportFullName: value });
            }}
          />
        ),
        render: (_, record) => (
          <div className="transfer__table-field">{record?.node?.passportFullName || '-'}</div>
        ),
      },
      {
        title: t('cms.booking.message_property.table.booking_status'),
        dataIndex: 'stages',
        key: 'stages',
        width: 228,
        filters: statusFilter,
        render: (_, record) => (
          <div className="transfer__table-field">
            {record?.node?.stage
              ? t(
                  `cms.booking.message_property.table.booking_status.${record?.node?.stage?.toLowerCase()}`,
                )
              : '-'}
          </div>
        ),
      },
      {
        title: t('cms.booking.message_property.table.create_date'),
        dataIndex: 'createDate',
        key: 'createDate',
        width: 184,
        sorter: tableData?.edges?.length > 0,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.createdAt
              ? formatDay(dayFormatType.UKWITHHM, record?.node?.createdAt)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.booking.message_property.table.commission'),
        dataIndex: 'commissionTier',
        key: 'commissionTier',
        width: 184,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.node?.commissions?.length > 0 &&
            record?.node?.commissions[0]?.currency &&
            record?.node?.commissions[0]?.totalPrice !== undefined
              ? record?.node?.commissions[0]?.currency + record?.node?.commissions[0]?.totalPrice
              : '-'}
          </span>
        ),
      },
    ],
    [tableData],
  );

  const handleOnRowClick = record => {
    const url =
      record.node?.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC
        ? generatePath('booking.detail', { id: record.node?.referenceId })
        : generatePath('booking.manualDetail', { id: record.node?.referenceId });
    history.push(url);
  };

  const [queryBookingData, { loading }] = useLazyQuery(QUERY_LISTORDERS, {
    fetchPolicy: 'no-cache',
    variables: {
      bookingJourneys,
    },
    onCompleted: data => {
      if (!exportLoading) {
        setTableData(data.listOrders);
      } else if (exportLoading && data.listOrders?.edges?.length > 0) {
        handleExportToExcel(
          t,
          data.listOrders,
          checkedIndex * 1000 + 1,
          bookingJourneys[0] === bookingJourneyType.SEMI_AUTOMATIC,
          () => {
            setExportLoading(false);
            setVisible(false);
          },
        );
      } else {
        setExportLoading(false);
        setVisible(false);
      }
    },
  });

  const handleTableChange = (page, tableFilter, sorter) => {
    const pageSize = page.pageSize !== filters?.pageSize ? page?.pageSize : filters?.pageSize;
    const pageNumber = filters?.pageSize && page.pageSize !== filters?.pageSize ? 1 : page.current;
    const stages = convertEmptyArrayToNull(tableFilter?.stages);
    const variables = {
      ...filters,
      ...tableFilter,
      pageNumber: pageNumber ?? 1,
      pageSize: pageSize ?? 10,
      sortDirection: sortDirectionMapping[sorter?.order],
      stages,
    };

    setFilters(variables);
    queryBookingData({ variables });
  };

  const handleExport = () => {
    if (tableData.totalCount > 0) {
      setVisible(true);
    }
  };

  const exportDataModal = useMemo(() => {
    let modalData = [];
    if (tableData?.totalCount % 1000 === 0) {
      modalData = range(tableData?.totalCount / 1000 - 1);
    } else {
      modalData = range(tableData?.totalCount / 1000);
    }

    return modalData.map(item => ({
      value: item,
    }));
  }, [tableData?.totalCount]);

  const handleChecked = idx => {
    setCheckedIndex(idx);
  };

  const handleDownloadBooking = () => {
    setExportLoading(true);
    queryBookingData({
      variables: {
        ...filters,
        pageSize: 1000,
        pageNumber: checkedIndex + 1,
      },
    });
  };

  const getColumns = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'value',
        key: 'value',
        render: (_, record) => {
          const value = record.value;
          return (
            <Radio
              checked={checkedIndex === value}
              className="radio-item"
              onClick={() => handleChecked(value)}
            >
              <span style={{ marginRight: 12 }}>{t('cms.sidebar.menu.booking')}</span>
              <span className="num-wrapper">{value * 1000 + 1}</span>
              <span className="dash">-</span>
              <span className="num-wrapper">
                {value === exportDataModal.length - 1 ? tableData?.totalCount : (value + 1) * 1000}
              </span>
            </Radio>
          );
        },
      },
    ],
    [checkedIndex, exportDataModal],
  );

  useEffect(() => {
    queryBookingData();
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <div className="booking__header-button">
        <Button icon="fund" type="link" onClick={() => setVisibleReport(true)}>
          {t('cms.booking.header.commission_report')}
        </Button>
        <Button icon="export" type="link" loading={exportLoading} onClick={handleExport}>
          {t('cms.reconciliation.landlord.export.export')}
        </Button>
      </div>
      <BookingTable
        columns={tableColumns}
        list={tableData}
        loading={loading && !exportLoading}
        filters={filters}
        handleTableChange={handleTableChange}
        onRowClick={handleOnRowClick}
      />
      <Drawer
        width={560}
        mask={false}
        destroyOnClose
        placement="right"
        onClose={() => setVisibleReport(false)}
        visible={visibleReport}
      >
        <CommissionReport t={t} />
      </Drawer>
      <Modal
        visible={visible}
        width={820}
        bodyStyle={{ padding: '32px 60px' }}
        okButtonProps={{ icon: 'download', loading: exportLoading }}
        okText="Download"
        onOk={handleDownloadBooking}
        onCancel={() => setVisible(false)}
        className="booking__modal-wrapper"
      >
        <header className="title">{t('cms.booking.export_data.title')}</header>
        <subtitle className="subtitle">{t('cms.booking.export_data.subtitle')}</subtitle>
        <section className="modal-content">
          <div className="export-header">
            <span className="available-export">{t('cms.booking.modal.header')}</span>
            <span className="bookings-count">
              {t('cms.booking.modal.export_count', { count: tableData?.totalCount })}
            </span>
          </div>
          <Table dataSource={exportDataModal} columns={getColumns} />
        </section>
      </Modal>
    </div>
  );
}
